import {
  Children,
  cloneElement,
  isValidElement,
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Eye, EyeOff, Loader2, X } from 'lucide-react';

/** Une clases ignorando falsy. Evita traer una dependencia solo para esto. */
export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// ── Botón ────────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'white' | 'onDark';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // La sombra teñida con el color de marca es lo que despega el botón del fondo
  // en vez de dejarlo pegado como un rectángulo de color.
  primary: 'bg-primary text-primary-fg hover:bg-primary-hover shadow-brand hover:shadow-lg',
  secondary: 'bg-primary-soft text-primary hover:brightness-95',
  ghost: 'text-fg-muted hover:bg-surface hover:text-fg',
  danger: 'bg-danger text-white hover:brightness-90 shadow-sm',
  outline: 'border border-border-strong text-fg hover:bg-surface hover:border-primary',
  // Los dos siguientes son para fondos oscuros. Sobre el navy de marca, un botón
  // azul se pierde; una píldora blanca con texto oscuro es lo que resalta.
  white: 'bg-white text-[#23293b] hover:bg-white/90 shadow-brand',
  onDark: 'border border-white/25 text-white hover:bg-white/10 hover:border-white/40',
};

// Más chicos y más anchos que antes: `text-sm` con padding generoso se lee como
// producto; `text-base` con altura fija se lee como formulario administrativo.
const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-[13px] gap-1.5',
  md: 'px-5 py-2.5 text-sm gap-2',
  lg: 'px-7 py-3.5 text-sm gap-2',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, fullWidth, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      // Un botón en curso se deshabilita: en una app clínica, un doble envío
      // puede significar dos registros del mismo síntoma y una alerta duplicada.
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center rounded-full font-semibold whitespace-nowrap',
        'transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
        // El hundido al presionar es la única confirmación táctil que tiene
        // alguien usando esto desde el teléfono.
        'active:scale-[0.97] motion-reduce:active:scale-100',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

// ── Contenedores ─────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  as: Tag = 'div',
  interactive,
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li';
  /** Se eleva al pasar el mouse. Solo para tarjetas que llevan a algún lado. */
  interactive?: boolean;
}) {
  return (
    <Tag
      className={cx(
        'bg-surface-raised border border-border rounded-lg shadow-sm',
        interactive && 'tr-lift cursor-pointer hover:border-primary/40',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * Etiqueta de sección en versalitas.
 *
 * Ordena la lectura sin gastar un título: dice de qué va el bloque antes de que
 * el ojo llegue al encabezado.
 */
export function Eyebrow({ children, onDark }: { children: ReactNode; onDark?: boolean }) {
  return (
    <p
      className={cx(
        'text-[11px] font-bold uppercase tracking-[0.16em]',
        onDark ? 'text-white/50' : 'text-fg-subtle',
      )}
    >
      {children}
    </p>
  );
}

/** Bloque de carga con brillo. Muestra la forma de lo que viene, no un spinner. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('tr-skeleton rounded-md', className)} aria-hidden />;
}

export function PageHeader({
  title,
  description,
  actions,
  icon,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** A la izquierda del título. El logo de a quién pertenece lo que se mira. */
  icon?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4 mb-6 animate-fade-up">
      <div className="flex items-start gap-3 min-w-0">
        {icon}
        <div className="min-w-0">
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">{title}</h1>
          {description && <p className="text-fg-muted mt-1 text-[15px]">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </header>
  );
}

// ── Formulario ───────────────────────────────────────────────────────────────

export function Field({
  label,
  hint,
  error,
  required,
  children,
  htmlFor,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
  /** Para que un campo pueda ocupar más de una columna dentro de una grilla. */
  className?: string;
}) {
  /**
   * Un identificador propio cuando nadie pasó uno.
   *
   * Sin esto, la etiqueta y el campo quedan sueltos: tocar el texto no enfoca
   * el campo y un lector de pantalla lo anuncia sin nombre. Pasaba en 22 de los
   * 115 campos de la aplicación, siempre por el mismo olvido — así que el
   * componente lo resuelve en vez de confiar en que cada uso se acuerde.
   *
   * `useId` da uno estable entre el servidor y el cliente, y no cambia entre
   * renderizados.
   */
  const generado = useId();
  const id = htmlFor ?? generado;

  return (
    <div className={cx('space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
        {required && (
          <span className="text-danger ml-0.5" aria-label="obligatorio">
            *
          </span>
        )}
      </label>

      {/* Se le presta el id al campo de adentro cuando no trae uno propio. Los
          que envuelven varios controles —un grupo de días, dos inputs— se
          quedan con el suyo y el `label` apunta al primero. */}
      {bindLabel(children, id)}

      {/*
        La ayuda va **debajo** del campo, no entre la etiqueta y el campo.
        Arriba empujaba el control hacia abajo, así que en una fila de la grilla
        el campo que tenía ayuda quedaba desalineado con sus vecinos —se veía
        como un error de maquetado y era solo esto—. Abajo, todos los controles
        de una fila arrancan a la misma altura y la ayuda se lee después de ver
        qué se pide, que además es cuando hace falta.
      */}
      {hint && <p className="text-[13px] text-fg-muted leading-snug">{hint}</p>}

      {error && (
        <p className="text-[13px] text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Le pone el id al primer control del árbol que no tenga uno. */
function bindLabel(children: ReactNode, id: string): ReactNode {
  let usado = false;

  const recorrer = (node: ReactNode): ReactNode => {
    if (usado || !isValidElement(node)) return node;

    const props = node.props as Record<string, unknown>;
    const tipo = typeof node.type === 'string' ? node.type : '';
    const esControl =
      ['input', 'select', 'textarea'].includes(tipo) ||
      (typeof node.type === 'function' &&
        ['Input', 'Select', 'Textarea'].includes((node.type as { name?: string }).name ?? ''));

    if (esControl) {
      usado = true;
      return props.id ? node : cloneElement(node, { id } as never);
    }

    if (props.children) {
      return cloneElement(node, {
        children: Children.map(props.children as ReactNode, recorrer),
      } as never);
    }

    return node;
  };

  return Children.map(children, recorrer);
}

const CONTROL_CLASSES =
  'w-full h-11 px-3 rounded-md border border-border bg-surface-raised ' +
  'placeholder:text-fg-subtle focus:border-primary focus:ring-2 focus:ring-primary/20 ' +
  'outline-none transition-colors disabled:bg-surface disabled:text-fg-muted';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cx(CONTROL_CLASSES, className)} {...props} />;
  },
);

/**
 * Campo de contraseña con el ojito para verla.
 *
 * No es un adorno: se escribe a ciegas, en un teléfono, con mayúsculas y un
 * caracter especial obligatorios. Sin poder mirar lo que se escribió, un dedo
 * mal apoyado se vuelve "contraseña incorrecta" sin forma de darse cuenta.
 *
 * Arranca oculta y vuelve a ocultarse sola no: queda como la dejó la persona.
 * Quien la mostró está mirando la pantalla.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function PasswordInput({ className, ...props }, ref) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cx(CONTROL_CLASSES, 'pr-11', className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((valor) => !valor)}
        aria-label={visible ? 'Ocultar la contraseña' : 'Ver la contraseña'}
        aria-pressed={visible}
        // Sin foco por tabulación: entre el campo y el botón de al lado, un
        // paso más en el medio molesta a quien navega con el teclado.
        tabIndex={-1}
        className="absolute right-0 top-0 h-11 w-11 grid place-items-center text-fg-subtle hover:text-fg transition-colors"
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
});

/**
 * El panel flotante de cualquier desplegable, dibujado fuera del árbol.
 *
 * Es la pieza que hace que una lista abierta dentro de un diálogo **salga** del
 * diálogo en vez de quedar recortada adentro, con dos barras de scroll una
 * dentro de otra.
 *
 * `position: fixed` no alcanza: el fondo del diálogo usa `backdrop-blur`, y
 * cualquier filtro convierte a ese elemento en el marco de referencia de sus
 * descendientes fijos. La lista quedaba posicionada respecto del diálogo —y
 * recortada por él— en vez de respecto de la pantalla. Con un portal el problema
 * desaparece de raíz; el precio es tener que medir dónde está el campo, porque
 * ya no hereda su posición.
 *
 * Se remide al scrollear y al cambiar el tamaño de la ventana: sin eso, un
 * desplegable abierto en una pantalla larga se queda flotando donde el campo ya
 * no está.
 */
export function DropdownPanel({
  anchor,
  open,
  onClose,
  children,
  maxHeight = 264,
}: {
  /** El campo debajo del cual se abre. */
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  maxHeight?: number;
}) {
  const [box, setBox] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setBox(null);
      return;
    }

    const measure = (): void => setBox(anchor.current?.getBoundingClientRect() ?? null);
    measure();

    // En captura para enterarse también del scroll de contenedores internos, no
    // solo del de la página.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, anchor]);

  if (!open || !box) return null;

  /**
   * Se acomoda al espacio que queda.
   *
   * Si abajo no entra, se abre hacia arriba; y en cualquier caso el resultado se
   * recorta contra la pantalla. Lo último importa más de lo que parece: dentro
   * de un diálogo con scroll propio, el campo puede estar fuera de la vista al
   * momento de medir, y sin el recorte la lista se dibujaba entera por debajo
   * del borde inferior — abierta, pero invisible.
   */
  const margin = 16;
  const gap = 6;
  const space = window.innerHeight - 2 * margin;

  const below = window.innerHeight - box.bottom - margin - gap;
  const above = box.top - margin - gap;
  const flip = below < 180 && above > below;

  const height = Math.min(maxHeight, space, Math.max(120, flip ? above : below));

  /**
   * Abriendo hacia arriba se ancla el **borde de abajo** al campo.
   *
   * Con `top` calculado como `box.top - height`, una lista de dos opciones se
   * dibujaba donde habría empezado una de doscientos píxeles: el panel quedaba
   * flotando lejos del campo, encimando lo que hubiera en el medio, y parecía
   * pertenecer a otro control. `bottom` deja que la caja crezca hacia arriba lo
   * que necesite y pegada a su campo siempre.
   */
  const style: CSSProperties = {
    left: Math.min(Math.max(margin, box.left), Math.max(margin, window.innerWidth - margin - box.width)),
    ...(flip
      ? { bottom: Math.max(margin, window.innerHeight - box.top + gap) }
      : { top: Math.min(box.bottom + gap, window.innerHeight - margin - height) }),
    width: box.width,
    maxHeight: height,
  };

  return createPortal(
    <>
      {/* Por encima del diálogo (z-50) para que el clic afuera cierre la lista
          y no atraviese hacia el formulario. */}
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        className="fixed z-[61] overflow-y-auto rounded-lg border border-border bg-surface-raised shadow-lg py-1.5 animate-in"
        style={style}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

/**
 * Desplegable propio, no el del navegador.
 *
 * La lista de un `<select>` nativo la dibuja el sistema operativo: no acepta
 * bordes redondeados, ni sombra, ni animación, ni la tipografía de la marca.
 * Por eso se veía como una caja gris cuadrada en medio de una interfaz que no
 * lo es.
 *
 * Mantiene la misma forma de uso —hijos `<option>`— para no tocar las veinte
 * pantallas que ya lo usaban; los lee y arma la lista él mismo.
 *
 * Deliberadamente conserva lo que el nativo hacía bien: se abre con Enter o
 * barra espaciadora, se cierra con Escape, y el `option` puede traer su propio
 * `style` (así el catálogo de tipografías muestra cada una en su letra).
 */
/**
 * Qué desplegable está abierto ahora mismo.
 *
 * Vive fuera de React porque el dato no es de ningún componente: es de la
 * pantalla. Un contexto obligaría a envolver la aplicación entera para algo que
 * son dos líneas.
 */
let abiertoActual: string | null = null;
const suscriptores = new Set<(id: string | null) => void>();

function useAbierto(): { id: string | null; set: (id: string | null) => void } {
  const [id, setId] = useState(abiertoActual);

  useEffect(() => {
    suscriptores.add(setId);
    return () => {
      suscriptores.delete(setId);
    };
  }, []);

  return {
    id,
    set: (next) => {
      abiertoActual = next;
      for (const avisar of suscriptores) avisar(next);
    },
  };
}

export function Select({
  className,
  children,
  value,
  onChange,
  disabled,
  id,
}: SelectHTMLAttributes<HTMLSelectElement>) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  /**
   * Uno abierto por vez en toda la pantalla.
   *
   * Cada desplegable tapa lo de atrás con una capa para cerrarse al tocar
   * afuera, así que abrir el de al lado costaba dos clics: el primero cerraba,
   * el segundo abría. Con un identificador compartido, abrir uno avisa y el
   * anterior se cierra solo.
   */
  const propio = useId();
  const abierto = useAbierto();

  useEffect(() => {
    if (open && abierto.id !== propio) setOpen(false);
  }, [open, abierto.id, propio]);

  const alternar = (): void => {
    if (open) {
      setOpen(false);
      abierto.set(null);
      return;
    }
    abierto.set(propio);
    setOpen(true);
  };

  const options = Children.toArray(children)
    .filter(isValidElement)
    .map((child) => {
      const props = child.props as { value?: string; children?: ReactNode; style?: CSSProperties };
      return { value: String(props.value ?? ''), label: props.children, style: props.style };
    });

  const selected = options.find((option) => option.value === String(value ?? ''));

  const choose = (next: string): void => {
    setOpen(false);
    // Se simula el evento del nativo para que las pantallas que ya leían
    // `event.target.value` sigan funcionando sin cambios.
    onChange?.({ target: { value: next } } as never);
  };

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={alternar}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cx(
          CONTROL_CLASSES,
          'pr-9 text-left flex items-center transition-colors',
          disabled && 'opacity-60 cursor-not-allowed',
          className,
        )}
        style={selected?.style}
      >
        <span className="truncate grow">{selected?.label ?? '—'}</span>
        <ChevronDown
          className={cx(
            'size-4 text-fg-subtle absolute right-3 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* La lista sale del contenedor: dentro de un diálogo quedaba recortada
          por el borde, con la mitad de las opciones fuera de alcance. */}
      <DropdownPanel anchor={container} open={open && !disabled} onClose={() => setOpen(false)}>
        <ul role="listbox">
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === selected?.value}
                onClick={() => choose(option.value)}
                style={option.style}
                className={cx(
                  'w-full text-left px-3 py-2 text-[15px] transition-colors',
                  option.value === selected?.value
                    ? 'bg-primary-soft text-primary font-medium'
                    : 'hover:bg-surface',
                )}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      </DropdownPanel>
    </div>
  );
}

/**
 * Interruptor de dos estados con su etiqueta.
 *
 * Para elegir entre dos opciones excluyentes donde una es claramente el
 * default. Un desplegable de dos ítems obliga a abrirlo para saber qué hay
 * dentro; el interruptor muestra los dos estados de una y se cambia con un clic.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={cx(
        'flex items-start gap-3',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'shrink-0 w-11 h-6 rounded-full relative transition-colors duration-200 mt-0.5',
          checked ? 'bg-primary' : 'bg-border-strong',
        )}
      >
        <span
          className={cx(
            'absolute top-0.5 size-5 rounded-full bg-white shadow transition-all duration-200',
            checked ? 'left-[1.375rem]' : 'left-0.5',
          )}
        />
      </button>
      <span className="min-w-0">
        <span className="block font-medium text-[15px] leading-tight">{label}</span>
        {hint && <span className="block text-sm text-fg-muted mt-0.5 leading-snug">{hint}</span>}
      </span>
    </label>
  );
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cx(CONTROL_CLASSES, 'h-auto min-h-24 py-2.5 leading-relaxed', className)}
        {...props}
      />
    );
  },
);

// ── Indicadores ──────────────────────────────────────────────────────────────

type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface text-fg-muted border-border',
  primary: 'bg-primary-soft text-primary border-transparent',
  success: 'bg-success-soft text-success border-transparent',
  warning: 'bg-warning-soft text-warning border-transparent',
  danger: 'bg-danger-soft text-danger border-transparent',
  info: 'bg-info-soft text-info border-transparent',
};

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Severidad clínica. Cada escalón se lee como peor que el anterior sin leer el texto. */
export function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, { label: string; className: string }> = {
    INFO: { label: 'Informativa', className: 'bg-[var(--tr-sev-info-bg)] text-[var(--tr-sev-info)]' },
    LOW: { label: 'Baja', className: 'bg-[var(--tr-sev-low-bg)] text-[var(--tr-sev-low)]' },
    MEDIUM: { label: 'Media', className: 'bg-[var(--tr-sev-medium-bg)] text-[var(--tr-sev-medium)]' },
    HIGH: { label: 'Alta', className: 'bg-[var(--tr-sev-high-bg)] text-[var(--tr-sev-high)]' },
    CRITICAL: { label: 'Crítica', className: 'bg-[var(--tr-sev-critical-bg)] text-[var(--tr-sev-critical)]' },
  };

  const entry = map[severity] ?? map.INFO!;

  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap',
        entry.className,
      )}
    >
      {entry.label}
    </span>
  );
}

export function Spinner({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-fg-muted">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="text-center py-14 px-6">
      {icon && <div className="text-fg-subtle mb-3 flex justify-center">{icon}</div>}
      <p className="font-semibold">{title}</p>
      {description && <p className="text-fg-muted text-sm mt-1 max-w-sm mx-auto">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Ocurrió un error inesperado';
  return (
    <div className="text-center py-14 px-6">
      <p className="font-semibold text-danger">No se pudo cargar</p>
      <p className="text-fg-muted text-sm mt-1">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-5" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────

/**
 * Confirmación de una acción difícil o imposible de deshacer.
 *
 * Se pide confirmación por lo que cuesta revertir, no por lo que suena grave:
 * borrar un paciente arrastra su historial clínico entero, y cerrar sesión en un
 * teléfono compartido puede dejar a alguien afuera de su programa sin recordar
 * la contraseña. Poner un cartel en cada botón entrena a la gente a apretar
 * "Aceptar" sin leer, que es peor que no tenerlo.
 *
 * El texto del botón dice qué va a pasar —"Borrar paciente", no "Aceptar"— para
 * que se pueda decidir sin releer el mensaje.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancelar',
  tone = 'primary',
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-[15px] text-fg-muted leading-relaxed">{description}</div>
    </Modal>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  /**
   * Se bloquea el scroll de la pantalla de atrás mientras el diálogo está
   * abierto.
   *
   * Sin esto, la rueda del mouse sobre el fondo mueve la página de abajo: el
   * diálogo queda flotando sobre contenido que se desplazó, y al cerrarlo uno
   * aparece en otro lugar de la lista sin entender por qué.
   *
   * El efecto va antes del `return` temprano porque los hooks no pueden
   * ejecutarse condicionalmente.
   */
  useEffect(() => {
    if (!open) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      /* No se cierra al tocar el fondo. Un clic al costado mientras se completa
         un formulario largo borraba todo lo escrito sin preguntar nada. Se sale
         por la X, por Cancelar o con Escape. */
    >
      <div
        className={cx(
          'bg-surface-raised w-full rounded-t-xl sm:rounded-lg shadow-lg',
          // En el teléfono sube desde abajo como una hoja; en escritorio escala
          // desde el centro. Cada gesto coincide con el que espera cada dispositivo.
          'max-h-[92vh] flex flex-col animate-slide-up sm:animate-in',
          wide ? 'sm:max-w-3xl' : 'sm:max-w-lg',
        )}
      >
        <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-border shrink-0">
          <h2 className="font-semibold text-lg">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="text-fg-muted hover:text-fg p-1 rounded-md"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="px-5 py-4 overflow-y-auto grow">{children}</div>

        {footer && (
          <footer className="px-5 py-4 border-t border-border flex justify-end gap-2 shrink-0 safe-bottom">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// ── Datos ────────────────────────────────────────────────────────────────────

export function Stat({
  label,
  value,
  hint,
  tone,
  interactive,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'default' | 'danger' | 'success';
  /** Lleva a algún lado. Se marca al pasar el mouse para que se note. */
  interactive?: boolean;
}) {
  return (
    <Card className={cx('p-5 tr-lift h-full', interactive && 'hover:border-primary cursor-pointer')}>
      <p className="text-[13px] text-fg-muted font-medium">{label}</p>
      <p
        className={cx(
          'text-3xl font-semibold mt-1 tabular-nums tracking-tight',
          tone === 'danger' && 'text-danger',
          tone === 'success' && 'text-success',
        )}
      >
        {value}
      </p>
      {hint && <p className="text-[13px] text-fg-muted mt-0.5">{hint}</p>}
    </Card>
  );
}

/**
 * Barra de gradiente de la marca. En el tema de Dx Tech es el magenta → azul →
 * celeste del PPT; en un programa con marca propia, el gradiente que cargó el
 * institución.
 */
export function GradientBar({ className }: { className?: string }) {
  return (
    <div
      className={cx('h-1 w-full rounded-full', className)}
      style={{ background: 'var(--tr-gradient)' }}
      aria-hidden
    />
  );
}
